import { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FiSearch } from "react-icons/fi";

import * as actionType from "../../constants/actionTypes";
import { getAll } from "../../actions/studensts";
import { tokenExpired } from "../token";

import Modal from "./Modal";
import JumpToModal from "./JumpToModal";
import Pagination from "./Pagination";
import Header from "./Header";
import { exportToExcel } from "../exportToExcel";
import UNAUTHORIZED from "../UNAUTHORIZED";
import MainStudentTable from "../MainStudentTable";
import StudentDetailsModal from "../StudentDetailsModal";

export default function Index() {
  const [user, setUser] = useState(JSON.parse(localStorage.getItem("profile")));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { students, count } = useSelector((state) => state.students);
  const [page, setPage] = useState(1);
  const [nextDisabled, setNextDisabled] = useState(false);
  const [search, setSearch] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(true);

  const [selected, setSelected] = useState();
  const [selectState, setSelectState] = useState("all");
  const [showPop, setShowPop] = useState(false);
  const [showJumpToPop, setShowJumpToPop] = useState(false);
  const [activeStudent, setActiveStudent] = useState(null);

  const logout = useCallback(() => {
    dispatch({ type: actionType.LOGOUT });
    navigate("/");
  }, [dispatch, navigate]);

  // new 15/8/2022 export to excel after select state and filter students
  const getSelectState = (state) => {
    setSelectState(state);
  };

  // clear students on mount
  useEffect(() => {
    dispatch({ type: actionType.CLEAR });
    setHasSearched(false);
  }, [dispatch]);

  // update students on search or page changes
  useEffect(() => {
    if (!hasSearched) return;
    tokenExpired(user, logout, navigate);
    setLoading(true);
    const timeOutId = setTimeout(() => {
      dispatch(getAll(page, search, "")).finally(() => setLoading(false));
    }, 500);
    setUser(user);
    return () => clearTimeout(timeOutId);
  }, [page, search, logout, navigate, dispatch, user, hasSearched]);

  // disabled the next button if in last page
  useEffect(() => {
    if (page === count?.maxPage) {
      setNextDisabled(true);
    } else {
      setNextDisabled(false);
    }
  }, [page, count?.maxPage]);

  return (
    <div>
      {!["admin", "auditor", "agent"].includes(user.value[0].role) ? (
        <UNAUTHORIZED />
      ) : (
        <div className="min-h-screen px-4 sm:px-8 py-6">
          {/* Modals */}
          {showPop && <Modal selected={selected} setShow={setShowPop} />}
          {showJumpToPop && (
            <JumpToModal selected={selected} setShow={setShowJumpToPop} />
          )}
          {activeStudent && (
            <StudentDetailsModal
              student={activeStudent}
              onClose={() => setActiveStudent(null)}
            />
          )}

          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white tracking-tight">Admin Table</h1>
            <p className="text-slate-400 text-sm mt-1">Manage, edit and export all student records</p>
          </div>

          {/* Toolbar */}
          <div className="mb-6">
            <Header
              selected={selected}
              setShowJumpToPop={setShowJumpToPop}
              setShowPop={setShowPop}
              setSearch={(val) => {
                if (val === "") {
                  dispatch({ type: actionType.CLEAR });
                  setHasSearched(false);
                } else {
                  setHasSearched(true);
                }
                setSearch(val);
              }}
              setPage={setPage}
              getSelectState={getSelectState}
              exportState={() => exportToExcel(selectState)}
            />
          </div>

          {/* Results */}
          <div className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm shadow overflow-hidden">
            {!hasSearched ? (
              <div className="flex flex-col items-center justify-center py-24 text-slate-400">
                <FiSearch className="text-5xl mb-4 opacity-40" />
                <p className="text-lg font-medium">Enter a name or ID to search</p>
                <p className="text-sm mt-1 opacity-60">Minimum 3 characters required</p>
              </div>
            ) : loading ? (
              <div className="flex flex-col items-center justify-center py-24">
                <div className="animate-spin rounded-full h-14 w-14 border-b-4 border-indigo-500 mb-4" />
                <p className="text-slate-400 text-base">Loading records…</p>
              </div>
            ) : students && students.length > 0 ? (
              <div className="p-3">
                <MainStudentTable
                  students={students}
                  onView={setActiveStudent}
                  selected={selected}
                  onSelect={setSelected}
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-24 text-slate-400">
                <p className="text-lg font-medium">No results found</p>
                <p className="text-sm mt-1 opacity-60">Try a different search term</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          <div className="py-5">
            <Pagination
              page={page}
              setPage={setPage}
              maxPage={count?.maxPage}
              nextDisabled={nextDisabled}
            />
          </div>
        </div>
      )}
    </div>
  );
}
